/** Create-ruleset dialog: name + optional codename + owning operator (or
 *  system). Rules are added on the edit page the dialog navigates to. */
import { Button, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as rulesetsApi from '@/api/notificationRulesets';
import { AppDialog } from '@/components/AppDialog';
import { FieldHelp } from '@/components/FieldHelp';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { singlePageTenant } from '@/scope/pageTenants';

import { SYSTEM_TENANT } from './CreateNotificationDialog';
import { RULESET_HELP } from './fieldHelp';

const CODENAME_RE = /^[A-Za-z][A-Za-z0-9_-]*$/;

export function RulesetCreateDialog({
  open,
  pageTenants,
  defaultSystem = false,
  onClose,
}: {
  open: boolean;
  /** The page's operators (D9): the one is pre-selected, all are featured; see
   *  `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  /** Open on System: the grid's operator filter is the System sentinel. */
  defaultSystem?: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const snackbar = useSnackbar();

  const [name, setName] = useState('');
  const [codename, setCodename] = useState('');
  // The page mounts this per open, so the seed is fresh every time.
  const [tenant, setTenant] = useState<TenantPickerValue | null>(() =>
    defaultSystem ? SYSTEM_TENANT : singlePageTenant(pageTenants),
  );
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const codenameInvalid = codename.trim() !== '' && !CODENAME_RE.test(codename.trim());

  const createMutation = useMutation({
    mutationFn: () =>
      rulesetsApi.create({
        tour_operator: tenant && tenant.id !== SYSTEM_TENANT.id ? tenant.id : null,
        name: name.trim(),
        ...(codename.trim() ? { codename: codename.trim() } : {}),
      }),
    onSuccess: (detail) => {
      void qc.invalidateQueries({ queryKey: ['notification_rulesets'] });
      snackbar.show(`Ruleset "${detail.name}" created.`, 'success');
      navigate(`/notifications/rulesets/${detail.id}`);
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 400 && e.body && typeof e.body === 'object') {
        const out: Record<string, string> = {};
        for (const [key, msg] of Object.entries(e.body as Record<string, unknown>)) {
          out[key] = Array.isArray(msg) ? msg.join(' ') : String(msg);
        }
        setServerErrors(out);
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Create failed.', 'error');
    },
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="New ruleset"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!name.trim() || codenameInvalid || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Create ruleset
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 0.5 }}>
        <TextField
          autoFocus
          size="small"
          label="Name"
          required
          value={name}
          error={Boolean(serverErrors.name)}
          helperText={serverErrors.name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          size="small"
          label="Codename (optional)"
          value={codename}
          error={codenameInvalid || Boolean(serverErrors.codename)}
          helperText={
            serverErrors.codename ??
            (codenameInvalid
              ? 'Must start with a letter; letters, digits, hyphens, underscores only.'
              : 'Stable alias for {% if-ruleset %} template tags; unique per operator.')
          }
          onChange={(e) => setCodename(e.target.value)}
          InputProps={{ endAdornment: <FieldHelp help={RULESET_HELP.codename} /> }}
        />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <TenantPicker
            value={tenant}
            featured={pageTenants}
            onChange={setTenant}
            label="Operator (empty = system)"
            extraOptions={[SYSTEM_TENANT]}
            popperZIndex={1500}
          />
          <FieldHelp help={RULESET_HELP.tenant} />
        </Stack>
      </Stack>
    </AppDialog>
  );
}
